import React from "react";
import { Space, DatePicker, InputNumber, Select, Button } from "antd";
import dayjs from "dayjs";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import { ReportDefinition, FilterType } from "../../services/reportRegistry";
import { PAYMENT_METHOD_OPTIONS } from "../../constants/paymentMethods";

const { RangePicker } = DatePicker;

interface ReportFiltersProps {
  report: ReportDefinition;
  filters: Record<string, any>;
  onChange: (filters: Record<string, any>) => void;
  onApply: () => void;
  onReset: () => void;
  staffList: Array<{ id: string; full_name: string }>;
  roomList: Array<{ id: string; room_number: string }>;
  departmentList: Array<{ id: string; code: string; name: string }>;
  bookingList: Array<{
    id: string;
    guest_name?: string;
    confirmation_number?: string;
    room_number?: string;
  }>;
}

const ReportFilterPanel: React.FC<ReportFiltersProps> = ({
  report,
  filters,
  onChange,
  onApply,
  onReset,
  staffList,
  roomList,
  departmentList,
  bookingList,
}) => {
  const updateFilter = (key: string, value: any) => {
    onChange({ ...filters, [key]: value });
  };

  const hasFilter = (f: FilterType) => report.filters.includes(f);

  return (
    <Space wrap style={{ marginBottom: 16 }}>
      {hasFilter("dateRange") && (
        <RangePicker
          value={
            filters.startDate && filters.endDate
              ? [dayjs(filters.startDate), dayjs(filters.endDate)]
              : null
          }
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              onChange({
                ...filters,
                startDate: dates[0].format("YYYY-MM-DD"),
                endDate: dates[1].format("YYYY-MM-DD"),
              });
            } else {
              onChange({ ...filters, startDate: undefined, endDate: undefined });
            }
          }}
        />
      )}

      {hasFilter("date") && (
        <DatePicker
          placeholder="Select date"
          value={filters.startDate ? dayjs(filters.startDate) : null}
          onChange={(d) => updateFilter("startDate", d ? d.format("YYYY-MM-DD") : undefined)}
        />
      )}

      {/* The ageing filters write the API's own parameter names (`as_of`,
          `terms_days`) rather than a UI alias: `filters` is passed through
          to the query string unchanged, so an alias would have to be
          rewritten somewhere else and would silently stop matching. */}
      {hasFilter("asOf") && (
        <DatePicker
          placeholder="Ageing as at"
          value={filters.as_of ? dayjs(filters.as_of) : null}
          onChange={(d) => updateFilter("as_of", d ? d.format("YYYY-MM-DD") : undefined)}
        />
      )}

      {hasFilter("termsDays") && (
        <InputNumber
          min={0}
          max={3650}
          addonAfter="days"
          placeholder="Terms"
          style={{ width: 150 }}
          value={filters.terms_days}
          onChange={(v) => updateFilter("terms_days", v === null ? undefined : v)}
        />
      )}

      {/* An empty picker means "let the server choose", and the server
          chooses the current year — which is what the placeholder says, so
          the default is visible instead of hiding behind an unset control. */}
      {hasFilter("year") && (
        <DatePicker
          picker="year"
          placeholder="This year"
          value={filters.year ? dayjs(String(filters.year), "YYYY") : null}
          onChange={(d) => updateFilter("year", d ? d.format("YYYY") : undefined)}
        />
      )}

      {hasFilter("department") && (
        <Select
          placeholder="Department"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 200 }}
          value={filters.departmentId}
          onChange={(v) => updateFilter("departmentId", v)}
          options={departmentList.map((d) => ({ label: d.name, value: d.id }))}
        />
      )}

      {hasFilter("month") && (
        <DatePicker
          picker="month"
          placeholder="Select month"
          value={filters.month ? dayjs(filters.month + "-01") : null}
          onChange={(d) =>
            updateFilter("month", d ? d.format("YYYY-MM") : undefined)
          }
        />
      )}

      {hasFilter("weekStart") && (
        <DatePicker
          placeholder="Week starting"
          value={filters.weekStart ? dayjs(filters.weekStart) : null}
          onChange={(d) =>
            updateFilter("weekStart", d ? d.format("YYYY-MM-DD") : undefined)
          }
        />
      )}

      {hasFilter("staff") && (
        <Select
          placeholder="Staff"
          allowClear
          style={{ width: 180 }}
          value={filters.staffId}
          onChange={(v) => updateFilter("staffId", v)}
          options={staffList.map((s) => ({ label: s.full_name, value: s.id }))}
        />
      )}

      {hasFilter("room") && (
        <Select
          placeholder="Room"
          allowClear
          style={{ width: 120 }}
          value={filters.roomId}
          onChange={(v) => updateFilter("roomId", v)}
          options={roomList.map((r) => ({
            label: r.room_number,
            value: r.id,
          }))}
        />
      )}

      {hasFilter("orderType") && (
        <Select
          placeholder="Order Type"
          allowClear
          style={{ width: 150 }}
          value={filters.orderType}
          onChange={(v) => updateFilter("orderType", v)}
          options={[
            { label: "Dine-In", value: "dine_in" },
            { label: "Take Away", value: "take_away" },
            { label: "Room Service", value: "room_service" },
          ]}
        />
      )}

      {hasFilter("paymentMethod") && (
        <Select
          placeholder="Payment Method"
          allowClear
          style={{ width: 160 }}
          value={filters.paymentMethod}
          onChange={(v) => updateFilter("paymentMethod", v)}
          options={[
            ...PAYMENT_METHOD_OPTIONS,
            { label: "Online", value: "online" },
            { label: "Room Charge", value: "room_charge" },
          ]}
        />
      )}

      {hasFilter("bookingId") && (
        <Select
          showSearch
          allowClear
          placeholder="Search guest / confirmation #"
          style={{ minWidth: 300 }}
          value={filters.bookingId}
          onChange={(v) => updateFilter("bookingId", v)}
          optionFilterProp="label"
          options={bookingList.map((b) => ({
            label: `${b.guest_name || "Unknown"}${
              b.confirmation_number ? ` — ${b.confirmation_number}` : ""
            }${b.room_number ? ` (${b.room_number})` : ""}`,
            value: b.id,
          }))}
        />
      )}

      <Button type="primary" icon={<SearchOutlined />} onClick={onApply}>
        Search
      </Button>
      <Button icon={<ReloadOutlined />} onClick={onReset}>
        Reset
      </Button>
    </Space>
  );
};

export default ReportFilterPanel;
